/**
 * OmniClinicalBridge PWA Install Prompt
 * OmniAlera Technologies Holdings Inc.
 * 
 * Prompts users to install the app for offline access
 * Critical for healthcare providers in low-connectivity areas
 */

import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone, Wifi, WifiOff, Check } from 'lucide-react';
import { isAppInstalled } from '../pwa/registerSW';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  useEffect(() => {
    // Check if already installed
    if (isAppInstalled()) {
      setIsInstalled(true);
      return;
    }
    
    // Check if previously dismissed
    const dismissedTime = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedTime) {
      const dismissedDate = new Date(dismissedTime);
      const daysSinceDismiss = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < 7) {
        setDismissed(true);
        return;
      }
    }
    
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      // Show prompt after a short delay
      setTimeout(() => setShowPrompt(true), 3000);
    };
    
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);
  
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setShowPrompt(false);
    setDeferredPrompt(null);
  };
  
  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa_prompt_dismissed', new Date().toISOString());
  };
  
  if (isInstalled || dismissed || !showPrompt) return null;
  
  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-white" />
            <span className="text-white font-semibold">Install App</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <h3 className="font-semibold text-gray-900 mb-2">
            Install OmniClinicalBridge
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Get instant access to clinical decision support, even without internet connection.
          </p>
          
          {/* Benefits */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                <WifiOff className="w-3 h-3 text-green-600" />
              </div>
              <span>Works offline in low-connectivity areas</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                <Download className="w-3 h-3 text-blue-600" />
              </div>
              <span>50 essential medicines at your fingertips</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center">
                <Check className="w-3 h-3 text-purple-600" />
              </div>
              <span>100 clinical conditions with protocols</span>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleInstall}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Install Now
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Offline Status Indicator
 * Shows connection status and sync state
 */
export const OfflineIndicator: React.FC<{
  isOnline: boolean;
  isFullySynced: boolean;
  lastSync?: Date | null;
}> = ({ isOnline, isFullySynced }) => {
  const [show, setShow] = useState(false);
  
  useEffect(() => {
    // Show indicator when going offline
    if (!isOnline) {
      setShow(true);
    } else {
      // Hide after coming back online with a delay
      const timer = setTimeout(() => setShow(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline]);
  
  if (!show && isOnline) return null;
  
  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ${
      show ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
    }`}>
      <div className={`px-4 py-2 rounded-full shadow-lg flex items-center gap-2 ${
        isOnline 
          ? 'bg-green-500 text-white' 
          : isFullySynced 
            ? 'bg-amber-500 text-white'
            : 'bg-red-500 text-white'
      }`}>
        {isOnline ? (
          <>
            <Wifi className="w-4 h-4" />
            <span className="text-sm font-medium">Back online</span>
          </>
        ) : (
          <>
            <WifiOff className="w-4 h-4" />
            <span className="text-sm font-medium">
              {isFullySynced ? 'Offline - Data available' : 'Offline - Limited access'}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Sync Data Button
 * Allows users to manually sync data for offline use
 */
export const SyncDataButton: React.FC<{
  onSync: () => Promise<void>;
  isSyncing: boolean;
  progress: number;
  lastSync?: Date | null;
}> = ({ onSync, isSyncing, progress, lastSync }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium text-gray-900">Offline Data</h4>
          <p className="text-sm text-gray-500">
            {lastSync 
              ? `Last synced: ${lastSync.toLocaleDateString()} ${lastSync.toLocaleTimeString()}`
              : 'Not synced yet'
            }
          </p>
        </div>
        <button
          onClick={onSync}
          disabled={isSyncing}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
            isSyncing
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          <Download className={`w-4 h-4 ${isSyncing ? 'animate-pulse' : ''}`} />
          {isSyncing ? `Syncing... ${progress}%` : 'Sync for Offline'}
        </button>
      </div>
      
      {isSyncing && (
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default PWAInstallPrompt;
